'use client'

import { Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * The equipment search — the first thing under the navigation.
 *
 * Owner's call for the redesign: nothing above it, the headline went, and the
 * field had to stop looking like a second navigation bar. So it is not a glass
 * pill any more (the header is one): it sits in a framed PANEL whose edge is
 * the brand gradient, and the field inside is a soft rectangle. The category
 * pills stay OUTSIDE the panel, in `RentalToolbar` — the owner wanted them
 * separate, not welded to the field.
 *
 * ## The rolling hint
 *
 * The placeholder is a sentence somebody might type — „Potrzebuję aparatu…" —
 * and it rolls every six seconds: the line slides up and the next one comes in
 * from below. A native `placeholder` cannot animate, so the hint is an
 * `aria-hidden` overlay that disappears the moment the field has text; the
 * field's accessible name stays „Szukaj sprzętu". Under reduced motion the
 * line changes without sliding.
 */
const HINTS = [
  'Potrzebuję aparatu…',
  'Potrzebuję mikrofonu…',
  'Potrzebuję światła…',
  'Potrzebuję statywu…',
] as const

const HINT_EVERY_MS = 6000
const HINT_SLIDE_MS = 560

export function RentalSearch({
  query,
  onQuery,
  className,
}: {
  query: string
  onQuery: (value: string) => void
  className?: string
}) {
  // `step` runs 0 → HINTS.length, where the last step is a copy of the first;
  // landing on it then snapping back to 0 without a transition is what makes
  // the loop seamless instead of rewinding through every line.
  const [step, setStep] = useState(0)
  const [snap, setSnap] = useState(false)

  useEffect(() => {
    let reset: ReturnType<typeof setTimeout> | undefined
    const timer = setInterval(() => {
      setSnap(false)
      setStep((prev) => {
        const next = prev + 1
        if (next === HINTS.length) {
          reset = setTimeout(() => {
            setSnap(true)
            setStep(0)
          }, HINT_SLIDE_MS + 60)
        }
        return next
      })
    }, HINT_EVERY_MS)
    return () => {
      clearInterval(timer)
      if (reset) clearTimeout(reset)
    }
  }, [])

  return (
    <search
      className={cn(
        'rounded-[1.75rem] border border-transparent p-2.5',
        '[background:linear-gradient(oklch(0.16_0.024_305),oklch(0.13_0.02_300))_padding-box,var(--flou-gradient)_border-box]',
        'shadow-[0_0_0_6px_color-mix(in_oklab,var(--flou-magenta)_6%,transparent),0_40px_100px_-40px_var(--flou-glow-core)]',
        className,
      )}
    >
      <div className="relative flex h-[4.5rem] items-center gap-3.5 rounded-[1.25rem] border border-white/[0.14] bg-white/[0.09] pr-2.5 pl-6 transition-colors focus-within:border-white focus-within:bg-white/[0.11]">
        <Search size={22} aria-hidden="true" className="shrink-0 text-white" />
        <div className="relative flex h-full min-w-0 flex-1 items-center">
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            aria-label="Szukaj sprzętu"
            autoComplete="off"
            className="h-full w-full bg-transparent font-masthead text-[1.25rem] font-medium text-white outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {query ? null : (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-0 h-[1.5em] -translate-y-1/2 overflow-hidden font-masthead text-[1.25rem] leading-[1.5em] font-medium text-white/60"
            >
              <span
                className={cn(
                  'flex flex-col',
                  snap
                    ? 'transition-none'
                    : 'transition-transform duration-[560ms] ease-[cubic-bezier(0.22,0.68,0.24,1)] motion-reduce:transition-none',
                )}
                style={{ transform: `translateY(${-1.5 * step}em)` }}
              >
                {[...HINTS, HINTS[0]].map((hint, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: the list repeats its first line on purpose
                  <span key={index} className="block h-[1.5em] whitespace-nowrap">
                    {hint}
                  </span>
                ))}
              </span>
            </span>
          )}
        </div>
        {query ? (
          <button
            type="button"
            onClick={() => onQuery('')}
            aria-label="Wyczyść szukanie"
            className="inline-flex size-12 shrink-0 items-center justify-center rounded-[0.875rem] bg-white/[0.08] text-white transition-colors hover:bg-white/[0.14]"
          >
            <X size={16} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </search>
  )
}
